import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useRef, useState } from "react";
import { Instagram, Facebook } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import heroImage from "@/assets/landing-hero.jpg";
import { LandingInstallButton } from "@/knowmeno/LandingInstallButton";
import { LandingPwaPrompt } from "@/components/LandingPwaPrompt";
import { KnowMenoLogo } from "@/knowmeno/KnowMenoLogo";
import { captureRefParam } from "@/lib/refParam";
import { capturePromoParam } from "@/lib/promoParam";

// asset: screenshot-report.png updated 2026-05-16 (v2 cache-bust)
import reportScreenshot from "@/assets/screenshot-report.png";
import scoreScreenshot from "@/assets/menopause-score-preview.png";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "KnowMeno — Track menopause, bring data to your doctor" },
      {
        name: "description",
        content:
          "The empowering companion for women navigating perimenopause and beyond. Daily tracking, AI-powered insights, and a report your doctor will actually use.",
      },
      {
        property: "og:title",
        content: "KnowMeno — Track menopause, bring data to your doctor",
      },
      {
        property: "og:description",
        content:
          "Daily tracking, AI insights, and a Symptom Intelligence Report that grows with you. Built by women, for women.",
      },
      { property: "og:url", content: "https://www.knowmeno.com/" },
      { property: "og:image", content: "https://www.knowmeno.com/og-image.png?v=2" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "KnowMeno — Track menopause, bring data to your doctor" },
      { name: "twitter:description", content: "Daily tracking, AI insights, and a Symptom Intelligence Report that grows with you." },
      { name: "twitter:image", content: "https://www.knowmeno.com/og-image.png?v=2" },
    ],
    links: [
      { rel: "canonical", href: "https://www.knowmeno.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            
            { q: "What if I don't want HRT?", a: "KnowMeno doesn't push any treatment. The Symptom Intelligence Report supports every conversation — HRT, non-hormonal options, lifestyle changes, whether more testing is needed. It gives you and your doctor the data to find what works for YOU." },
            { q: "Why do I have to wait to get my Symptom Intelligence Report?", a: "You don't wait for value — you get something useful from Day 1. Your personal Menopause Score appears the first time you log, along with a daily insight tied to what you logged. Your symptom heatmap starts coloring in by Day 7 and you can preview your Symptom Intelligence Report. The full 6-page PDF unlocks at Day 14 — that's the minimum window where the pattern data is reliable enough to share with a provider. By Day 90, you have the strongest version: trends, HRT response correlation, and the full picture. Short version: useful from Day 1, meaningful by Day 14, powerful by Day 90." },
            { q: "Is KnowMeno a medical device or a replacement for my doctor?", a: "No. KnowMeno is a personal wellness tracking tool — not a medical device. We help you organize the symptoms, patterns, and questions you bring to your doctor." },
            { q: "How much does KnowMeno cost?", a: "KnowMeno is completely free. Create an account and every feature is unlocked — no plan, no card, no trial countdown." },
            
          ].map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: { "@type": "Answer", text: it.a },
          })),
        }),
      },
    ],
  }),
  component: LandingPage,
});

// Brand palette
const PLUM = "#4B1F4F";
const ROSE = "#FF5C7A";
const ROSE_LIGHT = "#FFC1CD";
const CREAM = "#FAF7F5";
const CREAM_DEEP = "#FAF7F5";
const INK = "#4B1F4F";
const MUTED = "#6E4B71";
const LINE = "#E8D5DA";
const SAGE = "#FFC1CD";

const SERIF = '"Cormorant Garamond", "Playfair Display", Georgia, serif';
const SANS = '"Inter", system-ui, -apple-system, sans-serif';

function LandingPage() {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);

  // If already signed in, skip the landing page — unless the user explicitly
  // navigated here from a gated page (e.g. /choose-plan back button) with
  // ?stay=1, in which case let them browse the marketing site.
  useEffect(() => {
    captureRefParam();
    capturePromoParam();
    const stay =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("stay") === "1";
    if (stay) {
      setCheckingAuth(false);
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate({ to: "/app" });
      } else {
        setCheckingAuth(false);
      }
    });
  }, [navigate]);

  if (checkingAuth) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: CREAM,
          fontFamily: SANS,
          color: MUTED,
          fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <div style={{ background: CREAM, color: INK, fontFamily: SANS, minHeight: "100vh" }}>
      <FarewellToast />
      <Nav />
      <Hero />
      <DemoVideo />
      <EmpathyBlock />
      <WhatYoullSee />
      <NoOneAnswer />
      <FoundersNote />
      
      <JourneyTimeline />

      <EmailCapture />
      <TaglineAnchor />
      <FAQ />
      <TrustLine />
      <Footer />
      <LandingPwaPrompt />
    </div>
  );
}

function FarewellToast() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let flag: string | null = null;
    try { flag = window.sessionStorage.getItem("knowmeno_farewell"); } catch {}
    if (!flag) return;
    try { window.sessionStorage.removeItem("knowmeno_farewell"); } catch {}
    setShow(true);
    const t = window.setTimeout(() => setShow(false), 6000);
    return () => window.clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: 20,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        background: "#FAF7F5",
        color: "#4B1F4F",
        border: "1px solid #E8D5DA",
        borderLeft: "4px solid #FF5C7A",
        borderRadius: 12,
        padding: "12px 16px",
        boxShadow: "0 8px 24px rgba(61,40,23,0.12)",
        fontSize: 14,
        fontFamily: SANS,
        display: "flex",
        alignItems: "center",
        gap: 14,
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      <span>Your trial has been canceled. We hope to see you again.</span>
      <button
        aria-label="Dismiss"
        onClick={() => setShow(false)}
        style={{
          background: "none",
          border: "none",
          color: "#4B1F4F",
          fontSize: 18,
          lineHeight: 1,
          cursor: "pointer",
          padding: 0,
          opacity: 0.6,
        }}
      >
        ×
      </button>
    </div>
  );
}

function Logo({ size = 22, invert = false }: { size?: number; invert?: boolean }) {
  // Text wordmark matching the sign-up / sign-in header styling
  const iqColor = invert ? "#FFFFFF" : PLUM;
  return (
    <span
      className="meno-wordmark"
      aria-label="KnowMeno"
      style={{
        fontFamily: SANS,
        fontSize: size,
        fontWeight: 600,
        letterSpacing: "-0.01em",
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "baseline",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ color: iqColor }}>Know</span>
      <span style={{ color: ROSE }}>Meno</span>
      <sup
        style={{
          color: ROSE,
          fontSize: "0.38em",
          fontWeight: 600,
          lineHeight: 1,
          marginLeft: "0.1em",
          verticalAlign: "super",
        }}
      >
        ™
      </sup>
    </span>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const NAV_HEIGHT = 96;
  const NAV_BRAND = "#4B1F4F";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const scrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    setOpen(false);
  };

  const scrollToId = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  const linkStyle: React.CSSProperties = {
    color: "#4B1F4F",
    fontSize: 14,
    fontWeight: 400,
    textDecoration: "none",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: 0,
    fontFamily: SANS,
  };

  const ctaStyle: React.CSSProperties = {
    background: NAV_BRAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 500,
    padding: "9px 18px",
    borderRadius: 999,
    textDecoration: "none",
    display: "inline-block",
    fontFamily: SANS,
  };

  return (
    <>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#FFFFFF",
          borderBottom: "0.5px solid rgba(0,0,0,0.06)",
          boxShadow: scrolled ? "0 1px 8px rgba(0,0,0,0.06)" : "none",
          transition: "box-shadow 0.2s ease",
        }}
      >
        <nav
          className="topnav-row"
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: NAV_HEIGHT,
          }}
        >
          <a href="#top" onClick={scrollToTop} aria-label="KnowMeno home" style={{ display: "inline-flex", alignItems: "center" }}>
            <KnowMenoLogo height={140} className="topnav-logo" />
          </a>

          <div className="topnav-desktop" style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <a href="#how-it-works" onClick={scrollToId("how-it-works")} style={linkStyle}>How It Works</a>
            <a href="#faq" onClick={scrollToId("faq")} style={linkStyle}>FAQ</a>
            <a href="#faq" onClick={scrollToId("faq")} style={linkStyle}>FAQ</a>
            <Link to="/sign-in" style={{ ...linkStyle, color: MUTED }}>Sign in</Link>
            <Link to="/sign-up" style={ctaStyle}>Get started — free</Link>
          </div>

          <button
            className="topnav-burger"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            style={{
              display: "none",
              background: "none",
              border: "none",
              padding: 8,
              cursor: "pointer",
              color: "#4B1F4F",
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            ☰
          </button>
        </nav>
      </header>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 200,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "min(320px, 85vw)",
              height: "100%",
              background: "#FFFFFF",
              boxShadow: "-4px 0 16px rgba(0,0,0,0.08)",
              padding: "20px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#4B1F4F", padding: 4 }}
              >
                ×
              </button>
            </div>
            <a href="#how-it-works" onClick={scrollToId("how-it-works")} style={{ ...linkStyle, padding: "14px 4px", fontSize: 16 }}>How It Works</a>
            <a href="#faq" onClick={scrollToId("faq")} style={{ ...linkStyle, padding: "14px 4px", fontSize: 16 }}>FAQ</a>
            <Link to="/sign-in" onClick={() => setOpen(false)} style={{ ...linkStyle, color: MUTED, padding: "14px 4px", fontSize: 16 }}>Sign in</Link>
            <Link to="/sign-up" onClick={() => setOpen(false)} style={{ ...ctaStyle, marginTop: 16, textAlign: "center", padding: "12px 18px", fontSize: 14 }}>Get started — free</Link>
          </div>
        </div>
      )}

      <style>{`
        html { scroll-behavior: smooth; }
        #how-it-works, #faq { scroll-margin-top: ${NAV_HEIGHT + 8}px; }
        @media (max-width: 768px) {
          .topnav-desktop { display: none !important; }
          .topnav-burger { display: inline-flex !important; }
          .topnav-logo { height: 48px !important; }
          .topnav-row { min-height: 64px !important; }
        }
      `}</style>
    </>
  );
}

function Hero() {
  return (
    <section
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "40px 24px 48px",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.3fr) minmax(0, 1fr)",
          gap: 48,
          alignItems: "center",
        }}
        className="hero-grid"
      >
        <div>
          <h1
            style={{
              fontFamily: SERIF,
              fontWeight: 600,
              fontSize: "clamp(36px, 5.6vw, 60px)",
              lineHeight: 1.05,
              letterSpacing: "-0.025em",
              color: PLUM,
              margin: 0,
              marginBottom: 28,
            }}
          >
            Stop guessing. Start tracking. Build your Symptom Intelligence Report — starting today.
          </h1>

          <div style={{ display: "flex", flexDirection: "column", gap: 14, alignItems: "flex-start" }}>
            <Link
              to="/sign-up"
              style={{
                background: PLUM,
                color: "#fff",
                fontSize: 18,
                fontWeight: 600,
                padding: "16px 32px",
                borderRadius: 999,
                textDecoration: "none",
                display: "inline-block",
                boxShadow: "0 14px 30px -12px rgba(74,25,66,0.45)",
              }}
            >
              Start Now
            </Link>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.5,
                color: INK,
                opacity: 0.85,
                fontFamily: SANS,
                maxWidth: 540,
              }}
            >
              <div>Built for women across perimenopause, menopause, and postmenopause.</div>
            </div>
          </div>
        </div>

        <div
          className="hero-art"
          style={{
            position: "relative",
            borderRadius: 28,
            overflow: "hidden",
            boxShadow: "0 40px 80px -30px rgba(74,25,66,0.35)",
            background: CREAM_DEEP,
          }}
        >
          <img
            src={heroImage}
            alt="Warm abstract sunrise illustration in plum, rose, and sage — symbolizing a new chapter."
            width={1024}
            height={1024}
            fetchPriority="high"
            decoding="async"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </div>
      </div>

      <style>{`
        @media (max-width: 880px) {
          .hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .hero-art { display: none !important; }
        }
        @media (max-width: 600px) {
          .meno-wordmark { font-size: 20px !important; }
        }
      `}</style>
    </section>
  );
}

function DemoVideo() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoSrc = "/demo.mp4";
  const posterSrc = "/demo-poster.jpg";
  const PLAYBACK_RATE = 0.75;

  // Source is 1080x2400 (9:20 portrait phone footage)
  const ASPECT_PADDING = "222.22%";

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = PLAYBACK_RATE;
    }
  }, []);

  return (
    <section
      id="demo"
      style={{
        background: CREAM,
        padding: "48px 16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          margin: "0 auto",
          // Phone mockup frame
          padding: 10,
          borderRadius: 44,
          background: "linear-gradient(160deg, #2a1228 0%, #4a1942 60%, #2a1228 100%)",
          boxShadow:
            "0 30px 60px -25px rgba(74,25,66,0.45), inset 0 0 0 1px rgba(255,255,255,0.06)",
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            paddingTop: ASPECT_PADDING,
            borderRadius: 34,
            overflow: "hidden",
            background: "#000",
            boxShadow: "inset 0 0 0 2px rgba(0,0,0,0.6)",
          }}
        >
          <video
            ref={videoRef}
            src={videoSrc}
            poster={posterSrc}
            autoPlay
            loop
            muted
            playsInline
            onLoadedMetadata={(e) => {
              e.currentTarget.playbackRate = PLAYBACK_RATE;
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>
      </div>
    </section>
  );
}




function FoundersNote() {
  return (
    <section
      id="about"
      style={{
        background: CREAM,
        padding: "72px 24px",
      }}
    >
      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14,
              color: ROSE,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 18,
            }}
          >
            A note from our founder
          </div>
          <div
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(19px, 1.6vw, 22px)",
              lineHeight: 1.65,
              color: INK,
            }}
          >
            <p style={{ margin: "0 0 18px" }}>
              In my late 30s, my body stopped feeling like mine. Brain fog, hot flashes, periods
              gone haywire. My doctor said I was too young. Then came the prescriptions — first
              Estrogen, then a different Hormone Replacement Therapy — and 62 pounds I didn't ask for.
            </p>
            <p style={{ margin: "0 0 18px" }}>
              So I switched doctors. I got blood work. I tracked every symptom, every dose, every
              change. I walked into my next appointment with data — and I got my life back. It took
              six years. It shouldn't take anyone else that long.
            </p>
            <p style={{ margin: "0 0 28px" }}>
              Women I knew were stuck where I'd been — dismissed, misprescribed, losing time to a
              system that didn't have answers. So I built what I wished someone had handed me on
              day one.
            </p>
            <div
              style={{
                fontStyle: "italic",
                fontSize: 20,
                color: PLUM,
              }}
            >
              — Jannette, Founder
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}

function NoOneAnswer() {
  return (
    <section
      style={{
        background: CREAM,
        padding: "56px 24px",
      }}
    >
      <div style={{ maxWidth: 820, margin: "0 auto", textAlign: "center" }}>
        <h2
          style={{
            fontFamily: SERIF,
            fontWeight: 500,
            fontSize: "clamp(28px, 3.6vw, 40px)",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            color: PLUM,
            margin: "0 0 20px",
          }}
        >
          Whatever the right answer is for you, you'll know what to ask.
        </h2>
        <p
          style={{
            fontFamily: SERIF,
            fontSize: "clamp(17px, 1.4vw, 20px)",
            lineHeight: 1.6,
            color: INK,
            opacity: 0.9,
            margin: 0,
            maxWidth: 680,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Some women need HRT. Some don't. Some need different doses. Some need lifestyle changes,
          non-hormonal options, or more tests. KnowMeno doesn't push one answer — it gives you both
          the data and the questions so the conversation with your doctor can finally happen.
        </p>
      </div>
    </section>
  );
}

function WhatYoullSee() {
  const columns = [
    {
      title: "Your daily Score.",
      body:
        "One number every time you open the app — personalized to your baseline, your symptoms, your patterns. Up, down, or steady against your last 14 days. The signal that tells you whether your routine is working, whether the HRT dose is helping, whether avoiding that trigger actually matters.",
      stage: "Menopause Score during the transition. Vitality Score after postmenopause.",
    },
    {
      title: "Your Symptom Intelligence Report.",
      body:
        "A 6-page PDF. Page 1 hands your doctor a summary, your top discussion points, and the questions you want to ask. A 90-day heatmap shows hot flashes, mood, sleep, energy color-coded across every single day. Trends charts show symptom response before and after every HRT dose change. Plus cycle history, adherence, lab values, and your results explained in plain English — all in one shareable PDF.",
      stage: "Symptom Intelligence Report during the transition. Annual Wellness Summary after postmenopause.",
    },
  ];

  const phoneFrame = (inner: React.ReactNode) => (
    <div
      style={{
        width: 240,
        height: 500,
        borderRadius: 44,
        background: "#4B1F4F",
        padding: 10,
        boxShadow:
          "0 30px 60px -20px rgba(60, 20, 60, 0.25), 0 10px 25px -10px rgba(0,0,0,0.15)",
        position: "relative",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 36,
          background: "#ffffff",
          overflow: "hidden",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: 90,
            height: 22,
            borderRadius: 14,
            background: "#4B1F4F",
            zIndex: 2,
          }}
        />
        {inner}
      </div>
    </div>
  );

  return (
    <section style={{ background: CREAM, padding: "72px 24px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div
            style={{
              fontSize: 14,
              color: ROSE,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            The difference
          </div>
          <h2
            style={{
              fontFamily: SERIF,
              fontWeight: 500,
              fontSize: "clamp(32px, 4.4vw, 50px)",
              margin: 0,
              color: INK,
              letterSpacing: "-0.02em",
            }}
          >
            Your daily <span style={{ fontStyle: "italic", color: PLUM }}>Score.</span> Your <span style={{ fontStyle: "italic", color: PLUM }}>Symptom Intelligence Report.</span>
          </h2>
          <p
            style={{
              fontSize: 19,
              color: MUTED,
              marginTop: 14,
              fontFamily: SERIF,
              fontStyle: "italic",
              maxWidth: 820,
              marginLeft: "auto",
              marginRight: "auto",
              lineHeight: 1.5,
            }}
          >
            One personalized number every day. Six pages your doctor can't dismiss.
          </p>
        </div>

        {/* Two phone mockups */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 48,
            justifyItems: "center",
            alignItems: "start",
            maxWidth: 760,
            margin: "0 auto 64px",
          }}
        >
          {/* Left phone — Score placeholder */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 280 }}>
            {phoneFrame(
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#FBF7F4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={scoreScreenshot}
                  alt="KnowMeno Menopause Score card preview"
                  width={445}
                  height={618}
                  decoding="async"
                  loading="lazy"
                  style={{ width: "100%", height: "auto", display: "block", objectFit: "contain" }}
                />
              </div>
            )}
            <p
              style={{
                marginTop: 22,
                fontSize: 14,
                fontStyle: "italic",
                color: MUTED,
                textAlign: "center",
                lineHeight: 1.5,
                maxWidth: 260,
              }}
            >
              Your daily Score — see what's shifting today.
            </p>
          </div>

          {/* Right phone — Symptom Intelligence Report PDF */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 280 }}>
            {phoneFrame(
              <img
                src={reportScreenshot}
                alt="Symptom Intelligence Report"
                width={454}
                height={717}
                decoding="async"
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  objectPosition: "center",
                  display: "block",
                  background: "#ffffff",
                }}
              />
            )}
            <p
              style={{
                marginTop: 22,
                fontSize: 14,
                fontStyle: "italic",
                color: MUTED,
                textAlign: "center",
                lineHeight: 1.5,
                maxWidth: 260,
              }}
            >
              Your Symptom Intelligence Report — real evidence for every appointment.
            </p>
          </div>
        </div>

        {/* 2 symmetric text columns */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 48,
            maxWidth: 1000,
            margin: "0 auto",
          }}
        >
          {columns.map((c) => (
            <div key={c.title}>
              <h3
                style={{
                  fontFamily: SERIF,
                  fontSize: 24,
                  fontWeight: 700,
                  margin: 0,
                  marginBottom: 12,
                  color: INK,
                  letterSpacing: "-0.01em",
                }}
              >
                {c.title}
              </h3>
              <p style={{ fontSize: 15.5, lineHeight: 1.6, color: INK, opacity: 0.85, margin: 0 }}>
                {c.body}
              </p>
              <p
                style={{
                  fontFamily: SERIF,
                  fontStyle: "italic",
                  fontSize: 14.5,
                  color: MUTED,
                  marginTop: 14,
                  marginBottom: 0,
                  lineHeight: 1.55,
                  opacity: 0.9,
                }}
              >
                {c.stage}
              </p>
            </div>
          ))}
        </div>

        {/* Repeat CTA */}
        <div
          style={{
            marginTop: 48,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Link
            to="/sign-up"
            style={{
              background: PLUM,
              color: "#fff",
              fontSize: 18,
              fontWeight: 600,
              padding: "16px 32px",
              borderRadius: 999,
              textDecoration: "none",
              display: "inline-block",
              boxShadow: "0 14px 30px -12px rgba(74,25,66,0.45)",
            }}
          >
            Start Now
          </Link>
        </div>
      </div>

    </section>
  );
}


function FeatureCard({
  mockup,
  title,
  body,
}: {
  mockup: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 24,
        padding: 24,
        border: `1px solid ${LINE}`,
        boxShadow: "0 20px 50px -30px rgba(74,25,66,0.25)",
        display: "flex",
        flexDirection: "column",
        gap: 22,
      }}
    >
      <div
        style={{
          background: `linear-gradient(160deg, ${CREAM_DEEP} 0%, ${ROSE_LIGHT} 100%)`,
          borderRadius: 18,
          padding: 18,
          minHeight: 260,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {mockup}
      </div>
      <div style={{ padding: "4px 6px 8px" }}>
        <h3
          style={{
            fontFamily: SERIF,
            fontSize: 24,
            fontWeight: 500,
            margin: "0 0 10px",
            color: INK,
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </h3>
        <p style={{ fontSize: 17, lineHeight: 1.6, color: MUTED, margin: 0 }}>{body}</p>
      </div>
    </div>
  );
}

function MockupWordmark({ small = false }: { small?: boolean }) {
  return (
    <span
      style={{
        fontFamily: SANS,
        fontSize: small ? 11 : 13,
        fontWeight: 700,
        letterSpacing: "-0.01em",
        display: "inline-flex",
        alignItems: "baseline",
      }}
    >
      <span style={{ color: PLUM }}>Know</span>
      <span style={{ color: ROSE }}>Meno</span>
    </span>
  );
}

function HeatmapMockup() {
  const rows = ["Hot flashes", "Mood", "Sleep", "Energy", "Cramps", "Brain fog", "Anxiety"];
  const days = 30;
  // Deterministic pseudo-random severity 0-4
  const sev = (r: number, d: number) => {
    const v = (r * 31 + d * 17 + r * d * 3) % 5;
    return v;
  };
  const colorFor = (v: number) => {
    const palette = ["#FAF7F5", "#FFC1CD", "#FF5C7A", "#FF5C7A", "#4B1F4F"];
    return palette[v];
  };
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        width: "100%",
        maxWidth: 320,
        padding: "14px 14px 12px",
        boxShadow: "0 10px 24px -16px rgba(74,25,66,0.35)",
        fontFamily: SANS,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <MockupWordmark />
        <span style={{ fontSize: 9, color: MUTED, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          30 days
        </span>
      </div>
      <div style={{ fontSize: 11, color: INK, fontWeight: 600, marginBottom: 8 }}>
        Symptom heatmap
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "62px 1fr", gap: 6 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {rows.map((r) => (
            <div key={r} style={{ fontSize: 8.5, color: MUTED, lineHeight: 1, height: 14, display: "flex", alignItems: "center" }}>
              {r}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {rows.map((_, ri) => (
            <div key={ri} style={{ display: "grid", gridTemplateColumns: `repeat(${days}, 1fr)`, gap: 2 }}>
              {Array.from({ length: days }).map((__, di) => (
                <div
                  key={di}
                  style={{
                    height: 14,
                    background: colorFor(sev(ri, di)),
                    borderRadius: 2,
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
        <span style={{ fontSize: 9, color: MUTED }}>None</span>
        <div style={{ display: "flex", gap: 3, flex: 1, margin: "0 8px" }}>
          {[0, 1, 2, 3, 4].map((v) => (
            <div key={v} style={{ flex: 1, height: 8, background: colorFor(v), borderRadius: 2 }} />
          ))}
        </div>
        <span style={{ fontSize: 9, color: MUTED }}>Severe</span>
      </div>
    </div>
  );
}

function TrendsMockup() {
  // SVG line graph
  const W = 300;
  const H = 180;
  const pad = { l: 28, r: 12, t: 18, b: 26 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const pts = (seed: number) =>
    Array.from({ length: 13 }).map((_, i) => {
      const x = pad.l + (i / 12) * innerW;
      const base = 0.5 + Math.sin(i * 0.6 + seed) * 0.22 + Math.cos(i * 0.3 + seed * 1.7) * 0.12;
      const y = pad.t + (1 - Math.max(0.05, Math.min(0.95, base))) * innerH;
      return [x, y] as const;
    });
  const toPath = (p: readonly (readonly [number, number])[]) => {
    return p
      .map((pt, i) => {
        if (i === 0) return `M ${pt[0]},${pt[1]}`;
        const prev = p[i - 1];
        const cx = (prev[0] + pt[0]) / 2;
        return `Q ${cx},${prev[1]} ${cx},${(prev[1] + pt[1]) / 2} T ${pt[0]},${pt[1]}`;
      })
      .join(" ");
  };
  const line1 = pts(0.3);
  const line2 = pts(1.4);
  const line3 = pts(2.7);
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        width: "100%",
        maxWidth: 340,
        padding: 14,
        boxShadow: "0 10px 24px -16px rgba(74,25,66,0.35)",
        fontFamily: SANS,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <MockupWordmark />
        <span style={{ fontSize: 9, color: MUTED, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          12 weeks
        </span>
      </div>
      <div style={{ fontSize: 11, color: INK, fontWeight: 600, marginBottom: 6 }}>Trends</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {/* grid */}
        {[0.25, 0.5, 0.75].map((g) => (
          <line
            key={g}
            x1={pad.l}
            x2={W - pad.r}
            y1={pad.t + g * innerH}
            y2={pad.t + g * innerH}
            stroke={LINE}
            strokeWidth={1}
            strokeDasharray="2 3"
          />
        ))}
        {/* lines */}
        <path d={toPath(line1)} stroke={PLUM} strokeWidth={2.2} fill="none" strokeLinecap="round" />
        <path d={toPath(line2)} stroke={ROSE} strokeWidth={2.2} fill="none" strokeLinecap="round" />
        <path d={toPath(line3)} stroke={SAGE} strokeWidth={2.2} fill="none" strokeLinecap="round" />
        {/* annotation dot */}
        <circle cx={line1[6][0]} cy={line1[6][1]} r={4} fill={PLUM} />
        <line x1={line1[6][0]} x2={line1[6][0]} y1={line1[6][1] - 6} y2={pad.t + 4} stroke={PLUM} strokeWidth={1} strokeDasharray="2 2" />
        <text x={line1[6][0] + 4} y={pad.t + 10} fontSize={8} fill={PLUM} fontFamily={SANS} fontWeight={600}>
          HRT change
        </text>
        {/* x labels */}
        {["W1", "W4", "W8", "W12"].map((lbl, i) => (
          <text
            key={lbl}
            x={pad.l + (i / 3) * innerW}
            y={H - 8}
            fontSize={8}
            fill={MUTED}
            fontFamily={SANS}
            textAnchor={i === 0 ? "start" : i === 3 ? "end" : "middle"}
          >
            {lbl}
          </text>
        ))}
        {/* y labels */}
        {["High", "Med", "Low"].map((lbl, i) => (
          <text key={lbl} x={4} y={pad.t + i * (innerH / 2) + 3} fontSize={8} fill={MUTED} fontFamily={SANS}>
            {lbl}
          </text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
        {[
          { c: PLUM, l: "Sleep" },
          { c: ROSE, l: "Mood" },
          { c: SAGE, l: "Cycle" },
        ].map((it) => (
          <div key={it.l} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 9.5, color: MUTED }}>
            <span style={{ width: 10, height: 3, background: it.c, borderRadius: 2 }} />
            {it.l}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportMockup() {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 6,
        width: "100%",
        maxWidth: 240,
        aspectRatio: "8.5 / 11",
        padding: "18px 18px 14px",
        boxShadow: "0 18px 40px -18px rgba(74,25,66,0.45), 0 2px 0 rgba(74,25,66,0.06)",
        fontFamily: SANS,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <MockupWordmark small />
        <span style={{ fontSize: 7.5, color: MUTED, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Confidential
        </span>
      </div>
      <div
        style={{
          fontFamily: SERIF,
          fontSize: 14,
          color: PLUM,
          fontWeight: 600,
          lineHeight: 1.2,
          marginBottom: 4,
        }}
      >
        Doctor Health Summary
      </div>
      <div style={{ fontSize: 8.5, color: MUTED, marginBottom: 12 }}>Sample patient report</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        {[100, 92, 86, 96].map((w, i) => (
          <div key={i} style={{ height: 4, width: `${w}%`, background: CREAM_DEEP, borderRadius: 2 }} />
        ))}
      </div>

      <div style={{ fontSize: 8, fontWeight: 700, color: INK, marginBottom: 4 }}>
        Symptom frequency
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 42, marginBottom: 10 }}>
        {[28, 36, 18, 44, 24, 32, 22].map((h, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: h,
              background: i % 2 === 0 ? PLUM : ROSE,
              borderRadius: "2px 2px 0 0",
              opacity: 0.85,
            }}
          />
        ))}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
        {[94, 88, 78, 90, 82].map((w, i) => (
          <div key={i} style={{ height: 3, width: `${w}%`, background: CREAM_DEEP, borderRadius: 2 }} />
        ))}
      </div>

      <div style={{ marginTop: "auto", paddingTop: 8, borderTop: `1px solid ${LINE}` }}>
        <div style={{ fontSize: 7.5, color: MUTED, fontStyle: "italic", textAlign: "center" }}>
          Prepared for your next appointment
        </div>
      </div>
    </div>
  );
}

function SocialProof() {
  return (
    <section
      style={{
        background: "#FAF7F5",
        padding: "32px 24px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          fontSize: 13,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: PLUM,
          fontWeight: 500,
        }}
      >
        Built by women · For women · Backed by lived experience
      </div>
      <div
        style={{
          fontFamily: SERIF,
          fontStyle: "italic",
          fontSize: 22,
          color: INK,
          marginTop: 14,
          maxWidth: 700,
          marginLeft: "auto",
          marginRight: "auto",
          lineHeight: 1.4,
        }}
      >
        “Finally — a tool that takes us seriously, and gives our doctors something they can act on.”
      </div>
    </section>
  );
}




function CompareSimple() {
  const rows: { label: string; free: boolean; meno: boolean }[] = [
    { label: "Daily symptom logging", free: true, meno: true },
    { label: "6-page Symptom Intelligence Report", free: false, meno: true },
    { label: "Hormone Replacement Therapy (HRT) tracking", free: false, meno: true },
    { label: "Personalized daily Menopause / Vitality Score", free: false, meno: true },
  ];

  const Mark = ({ on }: { on: boolean }) => (
    <span
      style={{
        fontSize: on ? 22 : 20,
        color: on ? PLUM : MUTED,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {on ? "✓" : "—"}
    </span>
  );

  return (
    <section style={{ background: CREAM_DEEP, padding: "72px 24px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2
            style={{
              fontFamily: SERIF,
              fontWeight: 500,
              fontSize: "clamp(30px, 4vw, 46px)",
              margin: 0,
              color: INK,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            Free apps log.{" "}
            <span style={{ fontStyle: "italic", color: PLUM }}>KnowMeno explains.</span>
          </h2>
          <p style={{ fontSize: 19, color: MUTED, marginTop: 10, lineHeight: 1.5 }}>
            Symptom data is the start. The Symptom Intelligence Report is the point.
          </p>
        </div>

        <style>{`
          @media (max-width: 767px) {
            .compare-desktop { display: none !important; }
            .compare-mobile { display: flex !important; }
          }
          @media (min-width: 768px) {
            .compare-desktop { display: block !important; }
            .compare-mobile { display: none !important; }
          }
        `}</style>

        <div
          className="compare-desktop"
          style={{
            background: "#fff",
            borderRadius: 20,
            border: `1px solid ${LINE}`,
            overflow: "hidden",
            boxShadow: "0 20px 50px -30px rgba(74,25,66,0.25)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(220px, 2fr) minmax(110px, 1fr) minmax(110px, 1fr)",
            }}
          >
            <div style={{ padding: "18px 22px", background: CREAM_DEEP }} />
            <div
              style={{
                padding: "18px 12px",
                background: CREAM_DEEP,
                textAlign: "center",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: MUTED,
              }}
            >
              Free trackers
            </div>
            <div
              style={{
                padding: "14px 12px",
                background: PLUM,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <KnowMenoLogo height={44} invert />
            </div>


            {rows.map((r) => (
              <React.Fragment key={r.label}>
                <div
                  style={{
                    padding: "18px 22px",
                    borderTop: `1px solid ${LINE}`,
                    fontSize: 17,
                    color: INK,
                    fontWeight: 500,
                  }}
                >
                  {r.label}
                </div>
                <div
                  style={{
                    padding: "18px 12px",
                    borderTop: `1px solid ${LINE}`,
                    textAlign: "center",
                  }}
                >
                  <Mark on={r.free} />
                </div>
                <div
                  style={{
                    padding: "18px 12px",
                    borderTop: `1px solid ${LINE}`,
                    textAlign: "center",
                    background: "rgba(74,25,66,0.03)",
                  }}
                >
                  <Mark on={r.meno} />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="compare-mobile" style={{ display: "none", flexDirection: "column", gap: 12 }}>
          {rows.map((r) => (
            <div
              key={r.label}
              style={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: 8,
                padding: 14,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 700, color: INK, marginBottom: 12, lineHeight: 1.3 }}>
                {r.label}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: MUTED, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 6 }}>
                    Free trackers
                  </div>
                  <Mark on={r.free} />
                </div>
                <div style={{ textAlign: "center", background: "rgba(74,25,66,0.04)", borderRadius: 6, padding: "6px 4px" }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 6, minHeight: 18 }}>
                    <KnowMenoLogo height={28} />
                  </div>
                  <Mark on={r.meno} />
                </div>

              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function JourneyTimeline() {
  const milestones = [
    {
      day: "Day 1",
      essence: "Start Today",
      desc: "Sign up in under a minute. Today's Insight + your first Menopause Score appear with your first check-in. Your Symptom Intelligence Report begins building from your first entry.",
    },
    {
      day: "Day 7",
      essence: "Signals",
      desc: "A week of Score history. Your symptom heatmap starts coloring in. A preview of your Symptom Intelligence Report unlocks.",
    },
    {
      day: "Day 14",
      essence: "Unlock",
      desc: "Your full Symptom Intelligence Report unlocks. Ready to share with your doctor — and it deepens with every entry.",
    },
    {
      day: "Day 30",
      essence: "Patterns",
      desc: "Hormone Replacement Therapy (HRT) correlations, mood trends, refined cycle predictions, and a richer 30-day heatmap show what's working — and what isn't.",
    },
    {
      day: "Day 90",
      essence: "Evidence",
      desc: "Your Symptom Intelligence Report now reflects a full 90 days of data — the richest, most complete version to bring to your doctor. One tap to share — print, email, or hand them the PDF.",
    },
  ];

  return (
    <section id="how-it-works" style={{ background: CREAM_DEEP, padding: "72px 24px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div
            style={{
              fontSize: 14,
              color: ROSE,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            How it works
          </div>
          <h2
            style={{
              fontFamily: SERIF,
              fontSize: "clamp(28px, 3.6vw, 40px)",
              fontWeight: 500,
              margin: 0,
              color: INK,
              letterSpacing: "-0.01em",
            }}
          >
            Your <span style={{ fontStyle: "italic", color: PLUM }}>90-day journey</span>
          </h2>
          <p style={{ fontSize: 17, color: MUTED, marginTop: 8, marginBottom: 8 }}>
            Most features start Day 1. Your Symptom Intelligence Report unlocks at Day 14 and deepens through Day 90.
          </p>

          <p style={{ fontSize: 14, color: MUTED, fontStyle: "italic", margin: "0 0 36px" }}>
            One quick check-in per day. Every entry builds your report.
          </p>
        </div>

        <div
          className="timeline-row"
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 24,
          }}
        >
          <div
            className="timeline-line-h"
            style={{
              position: "absolute",
              top: 18,
              left: "12%",
              right: "12%",
              height: 2,
              background: LINE,
              zIndex: 0,
            }}
          />
          {milestones.map((m) => (
            <div
              key={m.day}
              style={{
                position: "relative",
                zIndex: 1,
                textAlign: "center",
                background: "transparent",
                border: "none",
                borderRadius: 16,
                padding: "0 8px",
                marginTop: 0,
                boxShadow: "none",
              }}
            >
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                <div
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#fff",
                    border: `2px solid ${PLUM}`,
                  }}
                />
              </div>
              <div
                style={{
                  fontFamily: SERIF,
                  fontSize: 28,
                  fontWeight: 600,
                  color: PLUM,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.1,
                }}
              >
                {m.day}
              </div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: INK,
                  marginTop: 6,
                  marginBottom: 8,
                }}
              >
                {m.essence}
              </div>
              <p style={{ fontSize: 13.5, lineHeight: 1.55, color: MUTED, margin: 0 }}>{m.desc}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            textAlign: "center",
            fontSize: 14,
            color: MUTED,
            fontStyle: "italic",
            margin: "32px auto 0",
            maxWidth: 720,
            lineHeight: 1.6,
          }}
        >
          In postmenopause? Your journey builds toward your Annual Wellness Summary — same
          structure, 12-month view, tuned to where your body is now.
        </p>

        <style>{`
          @media (max-width: 768px) {
            .timeline-row { grid-template-columns: 1fr !important; gap: 28px !important; }
            .timeline-line-h { display: none !important; }
          }
        `}</style>
      </div>
    </section>
  );
}





function EmailCapture() {
  const options = [
    { label: "Recognizing what's happening to my body", reason: "recognizing" },
    { label: "Not getting answers from my doctor", reason: "doctor_answers" },
    { label: "Wondering if HRT is working", reason: "hrt" },
    { label: "Just trying to track what I'm experiencing", reason: "tracking" },
    { label: "Something else — I'm not sure yet", reason: "unsure" },
  ];

  return (
    <section style={{ background: PLUM, padding: "56px 24px", color: "#fff" }}>
      <div style={{ maxWidth: 860, margin: "0 auto", textAlign: "center" }}>
        <h2
          style={{
            fontFamily: SERIF,
            fontWeight: 500,
            fontSize: "clamp(28px, 3.6vw, 40px)",
            margin: 0,
            marginBottom: 12,
            letterSpacing: "-0.02em",
          }}
        >
          What's <span style={{ fontStyle: "italic" }}>hardest for you</span> right now?
        </h2>
        <p
          style={{
            fontSize: 16,
            lineHeight: 1.6,
            opacity: 0.85,
            margin: 0,
            marginBottom: 32,
          }}
        >
          Tell us where you are. We'll meet you there.
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 12,
          }}
          className="qualifier-grid"
        >
          {options.map((o) => (
            <a
              key={o.reason}
              href={`/sign-up?reason=${o.reason}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 48,
                padding: "12px 22px",
                borderRadius: 999,
                background: CREAM,
                color: PLUM,
                fontFamily: SANS,
                fontSize: 15.5,
                fontWeight: 600,
                textDecoration: "none",
                lineHeight: 1.3,
                border: `1px solid ${CREAM}`,
                transition: "transform 120ms ease, background 120ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = CREAM_DEEP;
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = CREAM;
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {o.label}
            </a>
          ))}
        </div>
        <style>{`
          @media (max-width: 640px) {
            .qualifier-grid > a { width: 100%; }
          }
        `}</style>
      </div>
    </section>
  );
}


function TrustLine() {
  return (
    <section style={{ background: CREAM, padding: "40px 24px" }}>
      <p
        style={{
          fontFamily: SERIF,
          fontStyle: "italic",
          color: PLUM,
          opacity: 0.75,
          fontSize: "clamp(18px, 2vw, 22px)",
          lineHeight: 1.5,
          textAlign: "center",
          margin: 0,
          maxWidth: 720,
          marginInline: "auto",
        }}
      >
        Not a diagnosis. A clearer picture for you and your doctor.
      </p>
    </section>
  );
}

function EmpathyBlock() {
  return (
    <section style={{ background: "#FBEBE5", padding: "80px 24px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
        <h2
          style={{
            fontFamily: SERIF,
            fontStyle: "italic",
            fontWeight: 600,
            color: PLUM,
            fontSize: "clamp(30px, 4.2vw, 48px)",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
            margin: 0,
            marginBottom: 20,
          }}
        >
          Dismissed. Misprescribed. Told it's normal.
        </h2>
        <p
          style={{
            fontFamily: SANS,
            color: PLUM,
            opacity: 0.85,
            fontSize: 18,
            lineHeight: 1.6,
            margin: 0,
            maxWidth: 640,
            marginInline: "auto",
          }}
        >
          Every woman in this transition knows the script. KnowMeno gives you the daily signal and real evidence to walk into your next appointment impossible to dismiss.
        </p>
      </div>
    </section>
  );
}

function TaglineAnchor() {
  return (
    <section className="tagline-anchor" style={{ background: CREAM, padding: "72px 24px 32px" }}>
      <p
        style={{
          fontFamily: SERIF,
          fontStyle: "italic",
          color: PLUM,
          fontSize: "clamp(26px, 3.2vw, 38px)",
          lineHeight: 1.2,
          textAlign: "center",
          margin: 0,
          maxWidth: 1000,
          marginInline: "auto",
        }}
      >
        Know your body. Empower your life.
      </p>
      <style>{`
        @media (max-width: 760px) {
          .tagline-anchor { padding: 40px 24px 16px !important; }
          #faq { padding-top: 24px !important; }
        }
      `}</style>
    </section>
  );
}


function FAQ() {
  const items = [
    {
      q: "What if I don't want HRT?",
      a: "KnowMeno doesn't push any treatment. The Symptom Intelligence Report supports every conversation — HRT, non-hormonal options, lifestyle changes, whether more testing is needed. It gives you and your doctor the data to find what works for YOU.",
    },
    {
      q: "Why do I have to wait to get my Symptom Intelligence Report?",
      a: "You don't wait for value — you get something useful from Day 1. Your personal Menopause Score appears the first time you log, along with a daily insight tied to what you logged. Your symptom heatmap starts coloring in by Day 7 and you can preview your Symptom Intelligence Report. The full 6-page PDF unlocks at Day 14 — that's the minimum window where the pattern data is reliable enough to share with a provider. By Day 90, you have the strongest version: trends, HRT response correlation, and the full picture.\n\nShort version: useful from Day 1, meaningful by Day 14, powerful by Day 90.",
    },
    {
      q: "Is KnowMeno a medical device or a replacement for my doctor?",
      a: "No. KnowMeno is a personal wellness tracking tool — not a medical device. We help you organize the symptoms, patterns, and questions you bring to your doctor. Always work with a qualified healthcare provider for diagnosis and treatment.",
    },
    {
      q: "How much does KnowMeno cost?",
      a: "KnowMeno is completely free. Create an account and every feature is unlocked — including your Symptom Intelligence Report once you've logged for 14 days. No plan, no card, no trial countdown.",
    },
    {
      q: "What happens if my Hormone Replacement Therapy (HRT) changes mid-cycle?",
      a: "Just update your HRT entry — go to the HRT section in your profile and edit your dose, type, or schedule. KnowMeno tracks the change automatically and your Symptom Intelligence Report will note the regimen change with an effective date marker, so your provider can see exactly when you switched.\n\nSymptom data logged after the change is correlated to the new regimen for future trends. You don't need to start a new account or \"reset\" anything — just update and keep tracking.",
    },
    {
      q: "Who's behind KnowMeno?",
      a: "KnowMeno is built by a small team of women who've lived this — and who got tired of being told it was 'just stress.'",
    },
  ];
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" style={{ maxWidth: 820, margin: "0 auto", padding: "72px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <div
          style={{
            fontSize: 14,
            color: ROSE,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          Questions
        </div>
        <h2
          style={{
            fontFamily: SERIF,
            fontWeight: 500,
            fontSize: "clamp(32px, 4vw, 44px)",
            margin: 0,
            color: INK,
            letterSpacing: "-0.02em",
          }}
        >
          Frequently asked.
        </h2>
      </div>
      <div style={{ borderTop: `1px solid ${LINE}` }}>
        {items.map((it, i) => {
          const isOpen = open === i;
          return (
            <div key={i} style={{ borderBottom: `1px solid ${LINE}` }}>
              <button
                onClick={() => setOpen(isOpen ? null : i)}
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  padding: "22px 4px",
                  textAlign: "left",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  fontFamily: SERIF,
                  fontSize: 20,
                  color: INK,
                  fontWeight: 500,
                  letterSpacing: "-0.01em",
                }}
                aria-expanded={isOpen}
              >
                <span>{it.q}</span>
                <span
                  style={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: isOpen ? PLUM : ROSE_LIGHT,
                    color: isOpen ? "#fff" : ROSE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontFamily: SANS,
                    transition: "all .2s",
                  }}
                >
                  {isOpen ? "–" : "+"}
                </span>
              </button>
              {isOpen && (
                <div
                  style={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: MUTED,
                    paddingBottom: 24,
                    paddingRight: 44,
                    whiteSpace: "pre-line",
                  }}
                >
                  {it.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      style={{
        background: "#4B1F4F",
        color: "rgba(255,255,255,0.75)",
        padding: "56px 24px 40px",
        fontSize: 13,
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ minWidth: 200 }}>
          <div style={{ marginBottom: 12 }}>
            <KnowMenoLogo height={128} tagline invert />
          </div>
          <div style={{ opacity: 0.7 }}>© 2026 KnowMeno</div>
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <a href="/privacy" style={footerLink}>Privacy Policy</a>
          <a href="/terms" style={footerLink}>Terms of Service</a>
          <a href="/disclaimer" style={footerLink}>Disclaimer</a>
          <a href="#" style={footerLink} onClick={(e) => { e.preventDefault(); if (typeof window !== 'undefined' && (window as any).displayPreferenceModal) { (window as any).displayPreferenceModal(); } }}>Cookie Preferences</a>
          <a href="mailto:hello@knowmeno.com" style={footerLink}>Contact</a>
        </div>
      </div>
      <div
        style={{
          maxWidth: 1100,
          margin: "32px auto 0",
          display: "flex",
          gap: 28,
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <a
          href="https://www.tiktok.com/@knowmeno8"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="TikTok English (@knowmeno8)"
          style={socialIconLink}
          onMouseEnter={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "#FF5C7A"; }}
          onMouseLeave={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "rgba(255,255,255,0.85)"; }}
        >
          <span data-social-icon style={{ color: "rgba(255,255,255,0.85)", display: "inline-flex", transition: "color 0.15s" }}>
            <TikTokIcon />
          </span>
          <span style={socialLangLabel}>EN</span>
        </a>
        <a
          href="https://www.tiktok.com/@knowmeno.es"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="TikTok Spanish (@knowmeno.es)"
          style={socialIconLink}
          onMouseEnter={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "#FF5C7A"; }}
          onMouseLeave={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "rgba(255,255,255,0.85)"; }}
        >
          <span data-social-icon style={{ color: "rgba(255,255,255,0.85)", display: "inline-flex", transition: "color 0.15s" }}>
            <TikTokIcon />
          </span>
          <span style={socialLangLabel}>ES</span>
        </a>
        <a
          href="https://www.instagram.com/kno.wmeno/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram (@kno.wmeno)"
          style={socialIconLink}
          onMouseEnter={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "#FF5C7A"; }}
          onMouseLeave={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "rgba(255,255,255,0.85)"; }}
        >
          <span data-social-icon style={{ color: "rgba(255,255,255,0.85)", display: "inline-flex", transition: "color 0.15s" }}>
            <Instagram size={24} />
          </span>
        </a>
        <a
          href="https://www.facebook.com/share/1AjYM5AVsf/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Facebook (KnowMeno)"
          style={socialIconLink}
          onMouseEnter={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "#FF5C7A"; }}
          onMouseLeave={(e) => { (e.currentTarget.querySelector('[data-social-icon]') as HTMLElement).style.color = "rgba(255,255,255,0.85)"; }}
        >
          <span data-social-icon style={{ color: "rgba(255,255,255,0.85)", display: "inline-flex", transition: "color 0.15s" }}>
            <Facebook size={24} />
          </span>
        </a>
      </div>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          marginTop: 40,
          paddingTop: 24,
          borderTop: "1px solid rgba(255,255,255,0.1)",
          fontSize: 12,
          opacity: 0.6,
          lineHeight: 1.6,
        }}
      >
        KnowMeno is a wellness tracking tool and does not provide medical advice. Always consult
        your healthcare provider.
      </div>
    </footer>
  );
}

const footerLink: React.CSSProperties = {
  color: "rgba(255,255,255,0.85)",
  textDecoration: "none",
};

const socialIconLink: React.CSSProperties = {
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  textDecoration: "none",
};

const socialLangLabel: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: 1,
  textTransform: "uppercase",
  fontWeight: 600,
  lineHeight: 1,
  color: "rgba(255,255,255,0.85)",
};

function TikTokIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.84a8.16 8.16 0 0 0 4.77 1.52V6.91a4.85 4.85 0 0 1-1.84-.22Z"/>
    </svg>
  );
}
